import { Link } from 'react-router-dom'

import type { AdCampaignRow, AdvertisingOverview } from '@/api/advertisingOverview'
import { ADVERTISING_LEVEL_LABELS, type AttributionModel } from '@/api/types'
import { TmtAttributedBadge } from '@/components/TmtAttributedBadge'
import { Badge, Card, DataTable, EmptyState, formatInteger, formatRatio, type Column } from '@/design'
import { ProviderAttributedBadge } from '@/features/analytics/advertising/AdvertisingBadges'
import { formatMoney } from '@/features/analytics/advertising/metrics'
import { CardQueryBody } from '@/features/overview/CardQueryBody'

import { campaignName } from './campaignName'
import { UNDEFINED_FIGURE } from './definitions'
import { advertisingSectionError } from './errors'
import type { OverviewQueryLike } from './overviewQuery'
import { NotAvailable } from './SectionStates'

/** How many campaigns the page's table shows before "View all campaigns". */
export const CAMPAIGN_TABLE_ROWS = 10

function money(value: number | null, currency: string): string {
  return value === null ? UNDEFINED_FIGURE : formatMoney(value, currency)
}

function campaignColumns(currency: string): Column<AdCampaignRow>[] {
  return [
    {
      key: 'name',
      header: 'Campaign',
      cell: (row) => (
        <span className="aov-campaign">
          <span>{campaignName(row)}</span>
          {row.effectiveStatus && row.effectiveStatus !== 'ACTIVE' ? (
            <Badge tone="neutral">{row.effectiveStatus.toLowerCase().replace(/_/g, ' ')}</Badge>
          ) : null}
        </span>
      ),
      sortValue: (row) => campaignName(row),
    },
    { key: 'spend', header: 'Spend · Meta', align: 'end', cell: (row) => formatMoney(row.spendMinor, currency), sortValue: (row) => row.spendMinor },
    {
      key: 'tmtPurchases',
      header: 'Purchases · TmT',
      align: 'end',
      cell: (row) => formatInteger(row.tmt.purchases),
      sortValue: (row) => row.tmt.purchases,
    },
    {
      key: 'tmtRevenue',
      header: 'Revenue · TmT',
      align: 'end',
      cell: (row) => formatMoney(row.tmt.revenueMinor, currency),
      sortValue: (row) => row.tmt.revenueMinor,
    },
    {
      key: 'tmtCpa',
      header: 'CPA · TmT',
      align: 'end',
      cell: (row) => money(row.tmt.cpaMinor, currency),
      sortValue: (row) => row.tmt.cpaMinor ?? Number.POSITIVE_INFINITY,
    },
    {
      key: 'tmtRoas',
      header: 'ROAS · TmT',
      align: 'end',
      cell: (row) => (row.tmt.roas === null ? UNDEFINED_FIGURE : formatRatio(row.tmt.roas)),
      sortValue: (row) => row.tmt.roas ?? -1,
    },
    {
      key: 'metaPurchases',
      header: 'Purchases · Meta-reported',
      align: 'end',
      cell: (row) => formatInteger(row.providerReported.purchases),
      sortValue: (row) => row.providerReported.purchases,
    },
    {
      key: 'metaRevenue',
      header: 'Revenue · Meta-reported',
      align: 'end',
      cell: (row) => formatMoney(row.providerReported.revenueMinor, currency),
      sortValue: (row) => row.providerReported.revenueMinor,
    },
  ]
}

/** The KPI's attributed revenue against the campaign rows: the API's identity, every part shown, never netted. */
function KpiReconciliationNote({ overview }: { overview: AdvertisingOverview }) {
  const reconciliation = overview.reconciliation
  if (!reconciliation) return null
  const currency = overview.meta.currency
  const part = (label: string, value: { purchases: number; revenueMinor: number }, sign: '+' | '−' | '') => (
    <div>
      <dt>
        {sign ? (
          <>
            <span aria-hidden="true">{sign} </span>
            <span className="ci-visually-hidden">{sign === '+' ? 'plus ' : 'minus '}</span>
          </>
        ) : null}
        {label}
      </dt>
      <dd>
        {formatMoney(value.revenueMinor, currency)} · {formatInteger(value.purchases)} purchases
      </dd>
    </div>
  )
  return (
    <details className="aov-kpi-recon">
      <summary>How the campaign rows add up to the attributed revenue KPI</summary>
      <p className="aov-note">
        The KPI counts every TmT-attributed order on a channel with spend; a campaign row counts only orders whose
        visit resolved to that Meta campaign. The difference is listed here, never netted.
      </p>
      <dl className="aov-kpi-recon__parts">
        {part(`All ${overview.campaigns.length} campaign rows`, reconciliation.campaigns, '')}
        {part('Paid, no Meta campaign', reconciliation.unmatchedPaid, '+')}
        {part('Meta campaigns without spend in the range', reconciliation.adCampaignsWithoutSpend, '+')}
        {part('In a campaign row, but on a channel without spend', reconciliation.campaignCreditOffSpendRows, '−')}
        {part('Attributed revenue (the KPI)', { purchases: reconciliation.purchases, revenueMinor: reconciliation.attributedRevenueMinor }, '')}
      </dl>
    </details>
  )
}

/**
 * Every campaign with spend, most spend first: Meta's spend, TmT's attributed purchases,
 * revenue, CPA and ROAS, and Meta's own purchases and revenue in their own columns. The
 * first `CAMPAIGN_TABLE_ROWS`; "View all campaigns" opens the full campaign report.
 */
export function CampaignPerformanceCard({ query, model, viewAllHref }: { query: OverviewQueryLike; model: AttributionModel; viewAllHref: string }) {
  return (
    <Card
      title="Campaign performance"
      subtitle={`${ADVERTISING_LEVEL_LABELS.campaign}s with spend in the period · most spend first`}
      actions={
        <Link className="ci-button" to={viewAllHref}>
          View all campaigns
        </Link>
      }
    >
      <CardQueryBody query={query} loadingRows={6} errorDescription={advertisingSectionError(query.error, 'The campaign table could not be loaded.')}>
        {(overview) => {
          if (overview.notAvailable.ad_spend) return <NotAvailable />
          const rows = overview.campaigns.slice(0, CAMPAIGN_TABLE_ROWS)
          return (
            <div className="aov-campaigns">
              <div className="aov-badges">
                <TmtAttributedBadge model={model} lookbackDays={overview.meta.lookbackDays} className="adv-tmt-badge" />
                <ProviderAttributedBadge attributionWindows={overview.meta.attributionWindows} />
              </div>
              <DataTable
                caption="Campaign performance"
                rows={rows}
                rowKey={(row) => row.id}
                columns={campaignColumns(overview.meta.currency)}
                empty={<EmptyState variant="no-results" compact title="No campaign spend in this period" description="Meta reported no delivery in this range." />}
              />
              {overview.campaigns.length > rows.length ? (
                <p className="aov-note">
                  Showing the {rows.length} campaigns with the most spend, of {overview.campaigns.length}; sorting reorders
                  these {rows.length}. <Link to={viewAllHref}>View all campaigns</Link>
                </p>
              ) : null}
              <KpiReconciliationNote overview={overview} />
            </div>
          )
        }}
      </CardQueryBody>
    </Card>
  )
}
